import { createBrowserRouter } from 'react-router';
import { importPage } from '@/lib/import-page';
import { AppLayout } from '@/shell/AppLayout';
import { WorkspaceShell } from '@/shell/WorkspacePanel';
import { RequirePermission } from '@/shell/RequirePermission';
import type { Permission } from '@pm/shared';
import type { Session } from '@/features/auth/session';

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-canvas text-sm text-ink-muted">
    <span className="size-6 animate-spin rounded-full border-2 border-line border-t-secondary" aria-label="Loading" />
  </div>
);

/**
 * Where an in-flight navigation is going, so a page whose chunk went missing in
 * a deploy can be reloaded at its destination rather than at its origin.
 * Read at call time, by which point `router` exists.
 */
const pendingPath = () => {
  const next = router.state.navigation.location;
  return next ? `${next.pathname}${next.search}${next.hash}` : undefined;
};

/** Wraps a lazily loaded page in its route-level permission gate. */
const guarded = (
  load: () => Promise<{ Component: () => React.ReactNode }>,
  permissions: Permission[],
  also?: (session: Session) => boolean,
) => async () => {
  const mod = await importPage(load, pendingPath);
  return {
    Component: () => (
      <RequirePermission permissions={permissions} also={also}>
        <mod.Component />
      </RequirePermission>
    ),
  };
};

export const router = createBrowserRouter([
  { path: '/login', HydrateFallback: RouteFallback, lazy: () => importPage(() => import('@/features/auth/LoginPage'), pendingPath) },
  // A public form needs no account, so it sits outside the app and its session check.
  {
    path: '/forms/:token',
    HydrateFallback: RouteFallback,
    lazy: () => importPage(() => import('@/features/pm/forms/PublicFormPage'), pendingPath),
  },
  {
    path: '/',
    Component: AppLayout,
    HydrateFallback: RouteFallback,
    children: [
      {
        // Organizations for whoever manages them; everyone else's own workspaces.
        index: true,
        lazy: guarded(() => import('@/features/pm/organizations/OrganizationsPage'), ['pm.workspace.view']),
      },
      {
        path: 'workspaces',
        lazy: guarded(() => import('@/features/pm/workspaces/WorkspacesPage'), ['pm.workspace.view']),
      },
      {
        // A workspace's overview, its boards, docs, dashboards and workflows share the workspace
        // panel; an organization's page and its own docs, dashboards and workflows, its panel.
        Component: WorkspaceShell,
        children: [
          {
            path: 'organizations/:organizationId',
            lazy: guarded(() => import('@/features/pm/organizations/OrganizationPage'), ['pm.workspace.view']),
          },
          {
            path: 'workspaces/:workspaceId',
            lazy: guarded(() => import('@/features/pm/workspaces/WorkspacePage'), ['pm.workspace.view']),
          },
          {
            path: 'boards/:boardId',
            lazy: guarded(() => import('@/features/pm/boards/BoardPage'), ['pm.board.view']),
          },
          {
            path: 'docs/:docId',
            lazy: guarded(() => import('@/features/pm/docs/DocPage'), ['pm.board.view']),
          },
          {
            path: 'workflows/:workflowId',
            lazy: guarded(() => import('@/features/pm/workflows/WorkflowPage'), ['pm.board.view']),
          },
          {
            path: 'dashboards/:dashboardId',
            lazy: guarded(() => import('@/features/pm/dashboards/DashboardPage'), ['pm.board.view']),
          },
        ],
      },
      {
        path: 'my-day',
        lazy: guarded(() => import('@/features/pm/queue/QueuePage'), ['time.track']),
      },
      {
        path: 'templates',
        lazy: guarded(() => import('@/features/pm/templates/TemplateCenterPage'), ['pm.board.view']),
      },
      {
        path: 'automations',
        lazy: guarded(() => import('@/features/pm/automations/AutomationsPage'), ['pm.board.manage']),
      },
      {
        path: 'my-tasks',
        lazy: guarded(() => import('@/features/pm/tasks/MyTasksPage'), ['pm.item.update']),
      },
      {
        path: 'reports',
        lazy: guarded(() => import('@/features/pm/reports/ReportsPage'), ['pm.report.view']),
      },
      {
        path: 'hr/attendance',
        lazy: guarded(() => import('@/features/hr/attendance/AttendancePage'), ['hr.attendance.self']),
      },
      {
        path: 'hr/leave',
        lazy: guarded(() => import('@/features/hr/leave/LeavePage'), ['hr.leave.request']),
      },
      {
        path: 'hr/balances',
        lazy: guarded(() => import('@/features/hr/leave/BalancesPage'), ['hr.leave.request']),
      },
      {
        path: 'hr/employees',
        lazy: guarded(() => import('@/features/hr/employees/EmployeesPage'), ['hr.employee.view_team']),
      },
      {
        path: 'hr/employees/:userId',
        lazy: guarded(() => import('@/features/hr/employees/EmployeeProfilePage'), ['hr.employee.view_self']),
      },
      {
        path: 'me',
        lazy: guarded(() => import('@/features/hr/employees/EmployeeProfilePage'), ['hr.employee.view_self']),
      },
      {
        // Leads manage their own department's members without the permission.
        path: 'hr/departments',
        lazy: guarded(
          () => import('@/features/hr/departments/DepartmentsPage'),
          ['hr.departments.manage'],
          (session) => session.leadsDepartment,
        ),
      },
      {
        path: 'hr/holidays',
        lazy: guarded(() => import('@/features/hr/holidays/HolidaysPage'), ['hr.attendance.self']),
      },
      {
        path: 'hr/settings',
        lazy: guarded(() => import('@/features/hr/settings/HrSettingsPage'), ['hr.settings.manage']),
      },
      {
        path: 'admin/roles',
        lazy: guarded(() => import('@/features/admin/roles/RolesPage'), ['admin.roles.view']),
      },
      {
        path: 'admin/diagnostics',
        lazy: guarded(() => import('@/features/admin/diagnostics/DiagnosticsPage'), ['admin.logs.view']),
      },
      {
        path: 'admin/audit',
        lazy: guarded(() => import('@/features/admin/audit/AuditPage'), ['admin.audit.view']),
      },
    ],
  },
]);
